import React from 'react';
import { Zap } from 'lucide-react';

/**
 * "N/M créditos disponibles" pill for plan-limited features (image
 * generation, product search). `credits` is the shape returned by
 * GET /api/publications/credits or GET /api/research/credits:
 * { used, limit, remaining, unlimited, period_end, purchased_balance }.
 *
 * `purchased_balance` (image generations only) is the never-expiring
 * balance from one-time credit-pack purchases, on top of the plan's
 * monthly quota — pass `onBuyClick` to render a "Comprar" action once the
 * plan quota is exhausted.
 */
export default function CreditsBadge({ credits, label, className = '', onBuyClick, 'data-testid': testId }) {
  if (!credits) return null;

  if (credits.unlimited) {
    return (
      <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground ${className}`}>
        <Zap className="h-3.5 w-3.5 text-emerald-500" />
        {label}: ilimitado
      </span>
    );
  }

  const purchasedBalance = credits.purchased_balance || 0;
  const planExhausted = credits.remaining <= 0;
  const exhausted = planExhausted && purchasedBalance <= 0;
  const low = !planExhausted && credits.remaining <= Math.max(1, Math.ceil(credits.limit * 0.2));

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
        exhausted
          ? 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400'
          : low
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'border-border bg-card/60 text-muted-foreground'
      } ${className}`}
      title={credits.period_end ? `Se renueva el ${new Date(credits.period_end).toLocaleDateString('es-CO')}` : undefined}
    >
      <Zap className={`h-3.5 w-3.5 ${exhausted ? 'text-red-500' : low ? 'text-amber-500' : 'text-blue-500'}`} />
      {label}: {credits.remaining}/{credits.limit} disponibles
      {purchasedBalance > 0 && ` · +${purchasedBalance} comprados`}
      {exhausted && onBuyClick && (
        <button
          type="button"
          onClick={onBuyClick}
          data-testid={testId ? `${testId}-buy-btn` : undefined}
          className="ml-1 underline decoration-dotted underline-offset-2 hover:text-red-500 dark:hover:text-red-300"
        >
          Comprar más
        </button>
      )}
    </span>
  );
}
